'use client';

import { useState } from 'react';
import { useApiTranslation } from '@/lib/hooks/useApiTranslation';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { MarketItem } from '@/lib/types/markets/market';
import { Icon } from '@/lib/ui-kit/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/ui-kit/popover';
import { UiImage } from '@/lib/ui-kit/ui-image';
import { cn } from '@/lib/ui-kit/utils';

export function OrderFormHeader() {
  const [open, setOpen] = useState(false);
  const selectedOutcome = useSelectedOutcome();
  const event = useOrderFormStore.use.event();
  const setSelectedOutcomeId = useOrderFormStore.use.setSelectedOutcomeId();
  const { tr } = useApiTranslation();

  const outcomes = event?.markets ?? [];
  const hasMultiple = outcomes.length > 1;

  if (!event || !selectedOutcome) return null;

  const title = tr(selectedOutcome.translations, 'question', selectedOutcome.groupItemTitle);
  const headerImage =
    selectedOutcome.image || (!hasMultiple ? event.image : undefined) || '/evfallback.webp';

  const handleSelectOutcome = (outcomeId: string) => {
    setSelectedOutcomeId(outcomeId);
    setOpen(false);
  };

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="size-12 shrink-0 overflow-hidden rounded-[8px] border border-v2-border">
        <UiImage size={48} src={headerImage} fallback="/evfallback.webp" alt={title} />
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="text-[15px] font-semibold leading-5 text-v2-text-primary">{title}</span>
        {hasMultiple && (
          <Icon
            name="ChevronRight"
            size={12}
            className={cn(
              'shrink-0 rotate-90 text-v2-text-primary transition-transform duration-200',
              open && 'rotate-270',
            )}
          />
        )}
      </div>
    </div>
  );

  if (!hasMultiple) {
    return headerContent;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex w-full cursor-pointer items-center gap-3 text-left">
          {headerContent}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="scrollbar-thin flex max-h-[148px] !w-auto min-w-[144px] flex-col gap-4 overflow-y-auto rounded-[10px] border border-v2-border bg-v2-btn-secondary p-3 shadow-[1px_1px_8px_0px_rgba(0,0,0,0.2)]"
      >
        {outcomes.map((outcome: MarketItem) => {
          const isSelected = outcome.id === selectedOutcome.id;
          return (
            <button
              key={outcome.id}
              type="button"
              onClick={() => handleSelectOutcome(outcome.id)}
              className={cn(
                'cursor-pointer text-left text-[13px] font-medium leading-4 tracking-[-0.13px] whitespace-nowrap transition-colors',
                isSelected
                  ? 'text-v2-text-primary'
                  : 'text-v2-text-secondary hover:text-v2-text-primary',
              )}
            >
              {tr(outcome.translations, 'question', outcome.groupItemTitle)}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
